import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import "./Amazonfbm.css";
import Footer from "../Layouts/Footer";
import Canonical from "../../seo/Canonical";
import FBMFAQs from "./FBMFAQs";
import FBMHero from "./FBMHero";
import { useSectionInView, useStaggerReveal } from "../../hooks/useInViewOnce";

/* ════════════════════════════════════════════════
   ICONS
════════════════════════════════════════════════ */
const ArrowRight = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M3 8H13M9 4L13 8L9 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const CheckCircle = () => (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
    <circle cx="11" cy="11" r="11" fill="#28479e" opacity="0.12"/>
    <path d="M7 11L9.8 14L15 8" stroke="#28479e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const StorageIco = () => (
  <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
    <rect x="3" y="5" width="24" height="7" rx="2" fill="#28479e" opacity="0.12" stroke="#28479e" strokeWidth="1.7"/>
    <rect x="3" y="14" width="24" height="7" rx="2" fill="#28479e" opacity="0.12" stroke="#28479e" strokeWidth="1.7"/>
    <circle cx="8" cy="8.5" r="1.6" fill="#28479e"/>
    <circle cx="8" cy="17.5" r="1.6" fill="#28479e"/>
    <path d="M13 8.5H22M13 17.5H22" stroke="#28479e" strokeWidth="1.5" strokeLinecap="round" opacity="0.45"/>
    <path d="M3 24H27" stroke="#28479e" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);
const OrderIco = () => (
  <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
    <rect x="5" y="3" width="20" height="24" rx="2.5" fill="#28479e" opacity="0.1" stroke="#28479e" strokeWidth="1.7"/>
    <path d="M10 10H20M10 15H17M10 20H13" stroke="#28479e" strokeWidth="1.7" strokeLinecap="round"/>
    <circle cx="22" cy="22" r="5.5" fill="#28479e"/>
    <path d="M19.5 22L21.3 23.8L24.5 20.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const PackIco = () => (
  <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
    <path d="M15 3L27 9V21L15 27L3 21V9L15 3Z" fill="#28479e" opacity="0.1" stroke="#28479e" strokeWidth="1.7" strokeLinejoin="round"/>
    <path d="M15 3V27M3 9L15 15L27 9" stroke="#28479e" strokeWidth="1.7" strokeLinejoin="round"/>
  </svg>
);
const DispatchIco = () => (
  <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
    <path d="M5 10L15 4L25 10V22L15 28L5 22V10Z" fill="#28479e" opacity="0.08" stroke="#28479e" strokeWidth="1.7" strokeLinejoin="round"/>
    <path d="M10 17L13.5 20.5L20 14" stroke="#28479e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const ShipIco = () => (
  <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
    <rect x="3" y="11" width="18" height="12" rx="2" fill="#28479e" opacity="0.1" stroke="#28479e" strokeWidth="1.7"/>
    <path d="M21 15H27L29 20V23H21V15Z" fill="#28479e" opacity="0.1" stroke="#28479e" strokeWidth="1.7" strokeLinejoin="round"/>
    <circle cx="9.5" cy="24.5" r="2.7" fill="white" stroke="#28479e" strokeWidth="1.6"/>
    <circle cx="25" cy="24.5" r="2.7" fill="white" stroke="#28479e" strokeWidth="1.6"/>
    <path d="M3 16H21" stroke="#28479e" strokeWidth="1.5"/>
  </svg>
);
const ReturnIco = () => (
  <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
    <circle cx="15" cy="15" r="11" fill="#28479e" opacity="0.08" stroke="#28479e" strokeWidth="1.7"/>
    <path d="M9 15C9 11.69 11.69 9 15 9C17.4 9 19.5 10.4 20.55 12.5" stroke="#28479e" strokeWidth="1.9" strokeLinecap="round"/>
    <path d="M21 15C21 18.31 18.31 21 15 21C12.6 21 10.5 19.6 9.45 17.5" stroke="#28479e" strokeWidth="1.9" strokeLinecap="round"/>
    <path d="M20 9L21.5 13H17.5" stroke="#28479e" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M10 21L8.5 17H12.5" stroke="#28479e" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const services = [
  {
    icon: <StorageIco />,
    title: "Inventory Storage",
    desc: "Secure UK warehousing with real-time inventory visibility, organised stock locations, and accurate inventory management to support growing Amazon businesses.",
  },
  {
    icon: <OrderIco />,
    title: "Order Processing",
    desc: "Amazon orders sync automatically into our warehouse management system, enabling immediate processing, improved efficiency, and faster order fulfilment without delays.",
  },
  {
    icon: <PackIco />,
    title: "Pick & Pack Fulfilment",
    desc: "Every order is barcode verified, professionally packed, and quality checked to ensure accurate fulfilment and excellent customer satisfaction on delivery.",
  },
  {
    icon: <DispatchIco />,
    title: "Same-Day Dispatch",
    desc: "Orders received before the daily cut-off are processed, packed, and dispatched the same day to meet delivery commitments consistently.",
  },
  {
    icon: <ShipIco />,
    title: "Fast UK Shipping",
    desc: "Multi-carrier shipping through Royal Mail, DPD, DHL, and trusted couriers provides reliable tracked delivery with competitive nationwide shipping rates.",
  },
  {
    icon: <ReturnIco />,
    title: "Returns Management",
    desc: "Returned items are inspected, graded, and restocked or disposed of according to your instructions, helping recover value and improve inventory accuracy.",
  },
];

const benefits = [
  {
    title: "Lower Storage Costs",
    desc: "Pay only for space you use — no Amazon FBA long-term storage fees or seasonal surcharges.",
  },
  {
    title: "Greater Inventory Control",
    desc: "Your stock, your rules. Inspect it, redirect it, withdraw it — anytime, with no restrictions.",
  },
  {
    title: "Flexible Shipping Options",
    desc: "Choose from multiple courier services based on speed, price and destination — FBA can't offer that.",
  },
  {
    title: "Scalable Fulfilment",
    desc: "From 50 orders per month to 50,000 — our infrastructure grows with your Amazon business.",
  },
  {
    title: "Faster Delivery Options",
    desc: "Strategically located UK centres mean fast despatch times that protect your seller rating.",
  },
];


/* ════════════════════════════════════════════════
   COMPONENT
════════════════════════════════════════════════ */
export default function AmazonFBM() {
  const svc  = useStaggerReveal(services.length, 0.08, 90);
  const ben  = useStaggerReveal(benefits.length, 0.08, 90);

  const { ref: pricRef,  inView: pricIn  } = useSectionInView(0.1);

  // Mobile slider ref for services section
  const servicesSliderRef = useRef<HTMLDivElement>(null);
  const servicesAutoScrollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Auto-scroll functionality for mobile services section
  useEffect(() => {
    const slider = servicesSliderRef.current;
    if (!slider) return;

    const isMobile = window.innerWidth <= 768;
    if (!isMobile) return;

    let currentIndex = 0;
    const getCardWidth = () => {
      const containerWidth = slider.offsetWidth;
      return containerWidth * 0.85; // 85% width for each card
    };
    const gap = 16;

    const scrollToCard = (index: number) => {
      if (!slider) return;
      const cardWidth = getCardWidth();
      const scrollPosition = index * (cardWidth + gap);
      slider.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      });
    };

    const autoScroll = () => {
      currentIndex = (currentIndex + 1) % services.length;
      scrollToCard(currentIndex);
    };

    // Start auto-scroll after initial delay
    const startAutoScroll = setTimeout(() => {
      servicesAutoScrollIntervalRef.current = setInterval(autoScroll, 4000); // Scroll every 4 seconds
    }, 2000);

    // Cleanup
    return () => {
      clearTimeout(startAutoScroll);
      if (servicesAutoScrollIntervalRef.current) {
        clearInterval(servicesAutoScrollIntervalRef.current);
      }
    };
  }, []);

  return (
    <div className="fbm-root logistics-page">
      <Helmet>
        <meta name="keywords" content="Amazon FBM fulfilment UK, Fulfilment by Merchant, Amazon FBM services, e-commerce fulfilment, Amazon seller fulfilment, FBM UK" />
        <meta httpEquiv="X-Robots-Tag" content="index, follow" />
        <meta name="publisher" content="Pick Pack Pro" />
      </Helmet>
      <Canonical baseUrl="https://www.pickpackpro.co.uk" />
      <FBMHero />

      {/* ══ PERFORMANCE METRICS (content expansion) ═══════ */}
      <section className="fbm-section fbm-white fbm-performance">
        <div className="fbm-container">
          <div className="fbm-sec-head">
            <span className="fbm-tag">Performance</span>
            <h2 className="fbm-h2">Performance Metrics &amp; SLA Compliance</h2>
            <p className="fbm-sec-p">
              Successful Amazon FBM Fulfilment UK relies on consistent performance and strict SLA compliance. At Pick Pack
              Pro, we operate to Prime-grade standards with 99%+ on-time shipment rates, 98%+ Amazon Buy Shipping
              compliance, and under 0.5% order cancellations. Amazon FBM orders are imported in real time, batched by SKU,
              and processed by our experienced{' '}
              <Link to="/blog/pick-and-pack-fulfilment-uk-explained">pick and pack fulfilment</Link> team for fast,
              accurate dispatch. Labels are generated through Amazon Buy Shipping, ensuring compliant shipping services,
              instant tracking uploads, and protection of your Valid Tracking Rate. Weekend dispatch helps maintain
              delivery promises during peak trading periods. Returns are inspected, graded, and restocked within 24–48
              hours where appropriate. Detailed daily and weekly performance reports provide complete visibility into SLA
              compliance, fulfilment accuracy, exceptions, and continuous operational improvement.
            </p>
          </div>
        </div>
      </section>

      {/* ══ WHAT IS FBM ═══════════════════════════════════ */}
      <section className="fbm-section fbm-white">
        <div className="fbm-container">
          <div className="fbm-what-grid">
            <div className="fbm-what-img-col">
              <img src="/fulfilmentbymerchant/amazonfbm.webp" alt="Amazon FBM fulfilment UK warehouse picking - Expert Fulfilment by Merchant services" title="Amazon FBM Fulfilment Warehouse UK" className="fbm-what-img"  width="800" height="600" />
              <div className="fbm-what-badge">
                <span className="fbm-what-badge-title">FBM vs FBA</span>
                <span className="fbm-what-badge-sub">You stay in control</span>
              </div>
            </div>
            <div className="fbm-what-text">
              <span className="fbm-tag">What Is FBM?</span>
              <h2 className="fbm-h2">What is Amazon FBM Fulfilment?</h2>
              <p className="fbm-body-lg">
                Amazon FBM Fulfilment UK (Fulfilled by Merchant) means you or a trusted fulfilment partner stores, picks,
                packs, and ships orders directly to customers instead of using Amazon&apos;s warehouses. Our{' '}
                <Link to="/blog/amazon-fbm-fulfilment-service">Amazon FBM fulfilment service</Link> provides fast,
                reliable ecommerce fulfilment with accurate pick and pack, inventory management, and tracked UK delivery,
                giving sellers greater control, lower fulfilment costs, and flexible shipping solutions.
              </p>
              <div className="fbm-compare-cards">
                <div className="fbm-compare-card fbm-compare-fbm">
                  <span className="fbm-compare-label">FBM</span>
                  <ul>
                    {["You control storage", "Lower fees", "No FBA restrictions", "Flexible shipping"].map((t, i) => <li key={i}><CheckCircle />{t}</li>)}
                  </ul>
                </div>
                <div className="fbm-compare-card fbm-compare-fba">
                  <span className="fbm-compare-label">FBA</span>
                  <ul>
                    {["Amazon controls stock", "Higher storage fees", "Strict prep rules", "Limited courier choice"].map((t, i) => (
                      <li key={i}>
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="10" fill="#F1F5F9"/><path d="M7 13L13 7M7 7L13 13" stroke="#94A3B8" strokeWidth="1.8" strokeLinecap="round"/></svg>
                        {t}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <p className="fbm-body" style={{ marginTop: "6px" }}>
                Many businesses choose Amazon FBM Fulfilment UK to reduce FBA fees, retain complete inventory control, and
                offer more flexible shipping options. Compare models in our{' '}
                <Link to="/blog/fba-vs-fbm-vs-3pl-which-is-best-reddit-guide-uk">FBA vs FBM vs 3PL guide</Link> — it is an
                ideal solution for bulky, high-value, seasonal, or slow-moving products. Our Amazon FBM fulfilment services
                provide expert ecommerce fulfilment, secure warehousing, accurate pick and pack, and fast UK dispatch,
                helping Amazon sellers scale efficiently with reliable Fulfilment by Merchant solutions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ══ OPERATING MODEL ═══════════════════════════════ */}
      <section className="fbm-section fbm-off">
        <div className="fbm-container">
          <div className="fbm-sec-head">
            <span className="fbm-tag">How It Works</span>
            <h2 className="fbm-h2">Our Amazon FBM Operating Model: End-to-End Fulfilment</h2>
            <p className="fbm-sec-p">
              Our Amazon FBM Fulfilment UK service is built around efficient warehouse operations, transparent processes,
              and reliable order fulfilment. We begin by understanding your product catalogue, packaging requirements,
              shipping commitments, and business goals before creating a tailored Fulfilment by Merchant workflow — the
              same clarity you need when you{' '}
              <Link to="/blog/how-to-outsource-ecommerce-fulfilment">outsource ecommerce fulfilment</Link>. From
              inventory receiving and storage to pick and pack fulfilment, courier selection, and returns management,
              every stage is designed to maximise accuracy, improve delivery performance, and support your growth.
            </p>
          </div>
          <div className="fbm-two-col">
            <div className="fbm-col">
              <div className="fbm-col-title">How Our Amazon FBM Fulfilment Process Works</div>
              <p className="fbm-col-text">
                Amazon orders are imported in real time and processed through scanner-verified picking, professional
                packing, and fully tracked UK dispatch. Fast-moving products are strategically positioned for quicker
                picking, while clearly defined workflows minimise handling, reduce errors, and ensure every order is
                shipped on time.
              </p>
            </div>
            <div className="fbm-cards-slider">
              <div className="fbm-col">
                <div className="fbm-col-title">Receiving &amp; Inventory Management</div>
                <div className="fbm-icon-list">
                  <div className="fbm-icon-item"><CheckCircle /><span>Advance shipment notifications (ASNs), quantity checks, and quality inspections on arrival.</span></div>
                  <div className="fbm-icon-item"><CheckCircle /><span>Velocity-based inventory storage to improve picking efficiency and warehouse productivity.</span></div>
                </div>
              </div>
              <div className="fbm-col">
                <div className="fbm-col-title">Pick &amp; Pack Fulfilment</div>
                <div className="fbm-icon-list">
                  <div className="fbm-icon-item"><CheckCircle /><span>Barcode scanner verification to minimise picking errors.</span></div>
                  <div className="fbm-icon-item"><CheckCircle /><span>SKU-specific packaging, branded inserts, and customer packing instructions.</span></div>
                </div>
              </div>
              <div className="fbm-col">
                <div className="fbm-col-title">Shipping, Labels &amp; Tracking</div>
                <div className="fbm-icon-list">
                  <div className="fbm-icon-item"><CheckCircle /><span>Amazon Buy Shipping labels for policy compliance.</span></div>
                  <div className="fbm-icon-item"><CheckCircle /><span>Instant tracking uploads and automated customer notifications.</span></div>
                </div>
              </div>
              <div className="fbm-col">
                <div className="fbm-col-title">Returns Management</div>
                <div className="fbm-icon-list">
                  <div className="fbm-icon-item"><CheckCircle /><span>Returns inspected, graded, and processed within 24–48 hours.</span></div>
                  <div className="fbm-icon-item">
                    <CheckCircle />
                    <span>
                      Sellable stock is restocked quickly — see our guide on{' '}
                      <Link to="/blog/how-to-manage-ecommerce-returns">how to manage ecommerce returns</Link>.
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ TECHNOLOGY & VISIBILITY ════════════════════════ */}
      <section className="fbm-section fbm-white">
        <div className="fbm-container">
          <div className="fbm-sec-head">
            <span className="fbm-tag">Technology</span>
            <h2 className="fbm-h2">Technology, Visibility &amp; Control</h2>
            <p className="fbm-sec-p">
              Our Amazon FBM Fulfilment UK platform gives you complete visibility over inventory, orders, and warehouse
              operations without the need to micromanage. Using a cloud-based WMS, real-time reporting, and automated
              workflows, our Amazon FBM fulfilment services help you maintain inventory accuracy, improve operational
              efficiency, and make informed business decisions as you grow — including{' '}
              <Link to="/blog/multi-channel-fulfilment-uk">multi-channel fulfilment UK</Link> from one stock pool.
            </p>
          </div>
          <div className="fbm-two-col">
            <div className="fbm-col">
              <div className="fbm-col-title">Unified Stock Across Every Sales Channel</div>
              <p className="fbm-col-text">
                Real-time inventory synchronisation across Amazon, Shopify, TikTok Shop, eBay, and wholesale prevents
                overselling and cancelled orders.
              </p>
            </div>
            <div className="fbm-cards-slider">
              <div className="fbm-col">
                <div className="fbm-col-title">Live Exception Management</div>
                <div className="fbm-icon-list">
                  <div className="fbm-icon-item"><CheckCircle /><span>Early alerts for address errors, courier issues, and dispatch cut-off risks.</span></div>
                  <div className="fbm-icon-item"><CheckCircle /><span>Reduces shipping delays and protects Amazon seller performance.</span></div>
                </div>
              </div>
              <div className="fbm-col">
                <div className="fbm-col-title">Complete Audit Trail</div>
                <div className="fbm-icon-list">
                  <div className="fbm-icon-item"><CheckCircle /><span>Every barcode scan and warehouse action is recorded by user and device.</span></div>
                  <div className="fbm-icon-item"><CheckCircle /><span>Detailed reporting supports SLA compliance and continuous operational improvement.</span></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ PACKAGING & BRANDING ═══════════════════════════ */}
      <section className="fbm-section fbm-off">
        <div className="fbm-container">
          <div className="fbm-sec-head">
            <span className="fbm-tag">Brand</span>
            <h2 className="fbm-h2">Packaging, Presentation &amp; Customer Experience</h2>
            <p className="fbm-sec-p">
              Great packaging does more than protect your products — it strengthens your brand and improves the customer
              experience. Our Amazon FBM Fulfilment UK service combines professional pick and pack fulfilment with custom
              packaging solutions, helping every order arrive securely, consistently, and ready to impress while
              optimising shipping costs.
            </p>
          </div>
          <div className="fbm-two-col">
            <div className="fbm-col">
              <div className="fbm-col-title">Brand Control</div>
              <p className="fbm-col-text">
                Branded boxes, mailers, and custom packaging to reinforce your brand identity. SKU-level packing
                instructions with reference images for consistent presentation.
              </p>
            </div>
            <div className="fbm-cards-slider">
              <div className="fbm-col">
                <div className="fbm-col-title">Easy Returns</div>
                <div className="fbm-icon-list">
                  <div className="fbm-icon-item"><CheckCircle /><span>Branded return slips or QR code return options.</span></div>
                  <div className="fbm-icon-item"><CheckCircle /><span>Fast inspection, grading, and processing of returned items.</span></div>
                </div>
              </div>
              <div className="fbm-col">
                <div className="fbm-col-title">Right-Size Packaging</div>
                <div className="fbm-icon-list">
                  <div className="fbm-icon-item"><CheckCircle /><span>Right-size packing reduces wasted space and packaging materials.</span></div>
                  <div className="fbm-icon-item"><CheckCircle /><span>Helps lower courier costs by keeping parcels within optimal postage bands.</span></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ ONBOARDING & CHANGE MANAGEMENT ════════════════ */}
      <section className="fbm-section fbm-white">
        <div className="fbm-container">
          <div className="fbm-sec-head">
            <span className="fbm-tag">Onboarding</span>
            <h2 className="fbm-h2">From First Call to First Shipped Order</h2>
            <p className="fbm-sec-p">
              Getting started with our Amazon FBM Fulfilment UK service is simple and structured. From your initial
              consultation to your first dispatched order, we manage onboarding, inventory setup, packaging rules, courier
              preferences, and system integration. Use our checklist on{' '}
              <Link to="/blog/how-to-choose-fulfillment-partner">how to choose a fulfilment partner</Link> before you
              sign — then a dedicated account manager owns outcomes so most sellers go live within a week.
            </p>
          </div>
          <div className="fbm-two-col">
            <div className="fbm-col">
              <div className="fbm-col-title">Fast Pilot Launch</div>
              <p className="fbm-col-text">
                Launch with a carefully selected group of products, validating packaging, labels, courier services, and
                fulfilment workflows before scaling operations confidently.
              </p>
            </div>
            <div className="fbm-col">
              <div className="fbm-col-title">Seamless Onboarding</div>
              <p className="fbm-col-text">
                Your dedicated account manager configures inventory, integrations, courier preferences, and operational
                rules, ensuring every process is ready before launch.
              </p>
            </div>
            <div className="fbm-col">
              <div className="fbm-col-title">Operational Playbooks</div>
              <p className="fbm-col-text">
                Documented fulfilment procedures prepare your business for seasonal demand, promotional campaigns, and
                unexpected order increases while maintaining consistent service standards.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ══ PRICING & TRANSPARENCY ═════════════════════════ */}
      <section className="fbm-section fbm-off">
        <div className="fbm-container">
          <div className="fbm-sec-head">
            <span className="fbm-tag">Commercials</span>
            <h2 className="fbm-h2">Transparent Pricing &amp; Predictable Margins</h2>
            <p className="fbm-sec-p">
              Our Amazon FBM Fulfilment UK pricing is simple, transparent, and designed to protect your profit margins.
              Every fulfilment cost is clearly itemised, helping you forecast expenses accurately, reconcile invoices with
              confidence, and scale your Amazon business without unexpected charges or long-term contractual commitments —
              ideal for{' '}
              <Link to="/blog/fbm-fulfilment-for-fast-growing-sellers">FBM fulfilment for fast-growing sellers</Link>.
            </p>
          </div>
          <div className="fbm-two-col">
            <div className="fbm-col">
              <div className="fbm-col-title">What You Pay For</div>
              <p className="fbm-col-text">
                Every charge covers pick and pack, packaging materials, storage, and courier labels, with fully itemised
                invoices for complete financial transparency.
              </p>
            </div>
            <div className="fbm-col">
              <div className="fbm-col-title">Quarterly Cost Reviews</div>
              <p className="fbm-col-text">
                Regular pricing reviews identify savings through right-size packaging, courier optimisation, and
                operational improvements that reduce fulfilment costs without compromising service quality.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ══ SERVICES ══════════════════════════════════════ */}
      <section className="fbm-section fbm-off">
        <div className="fbm-container">
          <div className="fbm-sec-head">
            <span className="fbm-tag">Services</span>
            <h2 className="fbm-h2">Our Amazon FBM Fulfilment Services</h2>
            <p className="fbm-sec-p">
              Our Amazon FBM Fulfilment UK service provides everything you need to store, process, pick, pack, and dispatch
              Amazon orders with confidence. From secure warehousing and real-time inventory management to fast UK shipping
              and returns processing, our Amazon FBM fulfilment services help sellers deliver an exceptional customer
              experience while scaling efficiently — and when you need Prime badges, pair FBM with{' '}
              <Link to="/blog/what-is-seller-fulfilled-prime-uk-complete-guide-2026">Seller Fulfilled Prime UK</Link>.
            </p>
          </div>
          
          {/* Desktop Grid */}
          <div className="fbm-services-grid">
            {services.map((s, i) => (
              <div key={i} ref={el => { svc.refs.current[i] = el; }}
                className={`fbm-svc-card ${svc.visible[i] ? "fbm-in" : "fbm-out"}`}
                style={{ transitionDelay: `${i * 80}ms` }}>
                <div className="fbm-svc-icon">{s.icon}</div>
                <div>
                  <h3 className="fbm-svc-title">{s.title}</h3>
                  <p className="fbm-svc-desc">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile Slider */}
          <div className="fbm-services-mobile-slider" ref={servicesSliderRef}>
            {services.map((s, i) => (
              <div key={i} className="fbm-services-mobile-slide">
                <div className="fbm-services-mobile-card">
                  <div className="fbm-svc-icon">{s.icon}</div>
                  <div>
                    <p className="fbm-svc-title">{s.title}</p>
                    <p className="fbm-svc-desc">{s.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* ══ BENEFITS ══════════════════════════════════════ */}
      <section className="fbm-section fbm-blue-sec">
        <div className="fbm-container">
          <div className="fbm-benefits-layout">
            <div className="fbm-benefits-left">
              <span className="fbm-tag fbm-tag-white-outline">Why FBM?</span>
              <h2 className="fbm-h2 fbm-h2-white">Key Benefits of Amazon FBM Fulfilment for UK Sellers</h2>
              <p style={{ color: "rgba(255,255,255,.72)", fontSize: "1rem", lineHeight: 1.74, marginBottom: "28px" }}>
                Choosing Amazon FBM Fulfilment UK with Pick Pack Pro gives you greater control over inventory, fulfilment,
                and customer experience while reducing operational costs — including avoiding{' '}
                <Link to="/blog/reduce-amazon-fba-storage-fees-drip-feed" style={{ color: '#fbbf24' }}>
                  Amazon FBA storage fees
                </Link>{' '}
                with smarter inventory planning.
              </p>
              <Link to="/contact-fulfillment-services" className="fbm-btn fbm-btn-white" style={{ textDecoration: 'none' }}>
                Get started <ArrowRight />
              </Link>
            </div>
            <div className="fbm-benefits-right">
              {benefits.map((b, i) => (
                <div key={i} ref={el => { ben.refs.current[i] = el; }}
                  className={`fbm-ben-card ${ben.visible[i] ? "fbm-in" : "fbm-out"}`}
                  style={{ transitionDelay: `${i * 80}ms` }}>
                  <h4 className="fbm-ben-title">{b.title}</h4>
                  <p className="fbm-ben-desc">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      

      {/* ══ PRICING ═══════════════════════════════════════ */}
      <section className="fbm-section fbm-off">
        <div className="fbm-container">
          <div className={`fbm-pricing-card ${pricIn ? "fbm-in" : "fbm-out"}`} ref={pricRef}>
            <div className="fbm-pricing-left">
              <h2 className="fbm-h2">Get a Free Amazon FBM Fulfilment Quote</h2>
              <p className="fbm-body">
                Every Amazon business has unique fulfilment requirements. Tell us about your products, monthly order
                volumes, storage needs, and delivery expectations, and we&apos;ll prepare a tailored Amazon FBM Fulfilment
                UK solution with transparent pricing, flexible services, and expert support designed to help your business
                grow efficiently.
              </p>
              <ul className="fbm-pricing-list">
                {[
                  'No Hidden Fees — Transparent pricing with no setup costs or unexpected fulfilment charges.',
                  'Flexible Monthly Billing — Pay only for the services and storage your business actually uses.',
                  'Scales With Your Business — Flexible fulfilment capacity that grows alongside your order volumes.',
                  'Dedicated Account Manager — A single point of contact providing expert guidance and ongoing support.',
                  'No Long-Term Contracts — Adjust your services or cancel whenever your business requirements change.',
                ].map((t, i) => (
                  <li key={i}><CheckCircle /><span>{t}</span></li>
                ))}
              </ul>
            </div>
            <div className="fbm-pricing-right">
              <div className="fbm-pricing-box">
                <h3 className="fbm-pricing-box-title">Request Your Quote</h3>
                <p className="fbm-pricing-box-sub">Tailored to your order volume and storage needs. No obligation.</p>
                 <Link to="/contact-fulfillment-services" className="fbm-btn fbm-btn-solid fbm-btn-full" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>Get a free quote <ArrowRight /></Link>
                 <Link to="/book-a-free-call" className="fbm-btn fbm-btn-ghost fbm-btn-full" style={{ marginTop: "12px", textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Book a discovery call</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ FAQ (homepage style + schema) ═════════════════ */}
      <FBMFAQs />

  
      <Footer />
    </div>

);
}
